import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Layers, Zap, Shield, Network, Lock, Vote, Database, Cpu } from 'lucide-react';
import './Features.css';

const Features = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: <Layers size={32} />,
      title: 'Multi-L1 Settlement Engine',
      description: 'Simultaneous state settlement across 8+ Layer-1 blockchains including Ethereum, BNB Chain, Solana, Avalanche, and more with unified proof aggregation.',
      metrics: '30s cross-L1 anchoring',
    },
    {
      icon: <Network size={32} />,
      title: 'Unified Liquidity Layer',
      description: 'Aggregate liquidity from DeFi AMMs and centralized exchanges with atomic swaps, HTLCs, and synchronized orderbooks across all connected networks.',
      metrics: 'Cross-chain DEX',
    },
    {
      icon: <Cpu size={32} />,
      title: 'Dual Runtime NVM',
      description: 'Execute smart contracts in both EVM and WASM environments. Deploy once in Solidity, Rust, or Move and access all supported chains instantly.',
      metrics: 'EVM + WASM support',
    },
    {
      icon: <Shield size={32} />,
      title: 'MEV Resistance',
      description: 'Encrypted mempool with threshold encryption, VRF randomization, and proposer-builder separation ensures fair transaction ordering.',
      metrics: 'Zero front-running',
    },
    {
      icon: <Zap size={32} />,
      title: 'Intent-Based Routing',
      description: 'Sign once, execute everywhere. Our intent router finds optimal cross-chain execution paths with best pricing and minimal latency.',
      metrics: 'One-click DeFi',
    },
    {
      icon: <Vote size={32} />,
      title: 'Hybrid Consensus',
      description: 'PoS + BFT consensus with validator quorum, BLS signatures, and slashing mechanisms for maximum security and decentralization.',
      metrics: '1-2s finality',
    },
    {
      icon: <Database size={32} />,
      title: 'Advanced DA Layer',
      description: 'Data availability mirrored across distributed nodes and Ethereum with multi-tier oracle feeds from Chainlink, Pyth, and Nomoex Exchange.',
      metrics: '99.99% uptime',
    },
    {
      icon: <Lock size={32} />,
      title: 'Institutional Security',
      description: 'Audited by CertiK, PeckShield, and Trail of Bits with a $500K bug bounty program and comprehensive cross-chain monitoring.',
      metrics: 'Battle-tested',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="features" className="features-alien" data-testid="features-section" ref={ref}>
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          className="section-header-alien"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="section-tag">
            <div className="tag-line"></div>
            <span className="tag-text">CORE CAPABILITIES</span>
            <div className="tag-line"></div>
          </div>
          <h2 className="section-title-alien">
            <span className="title-normal">KEY</span>
            <span className="title-highlight">FEATURES</span>
          </h2>
          <p className="section-subtitle-alien">
            Advanced blockchain infrastructure engineered for the next generation of decentralized finance
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="features-grid-alien"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card-alien"
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              data-testid={`feature-card-${index}`}
            >
              <div className="card-corners">
                <div className="corner tl"></div>
                <div className="corner tr"></div>
                <div className="corner bl"></div>
                <div className="corner br"></div>
              </div>
              
              <div className="feature-icon-alien">
                {feature.icon}
                <div className="icon-glow"></div>
              </div>
              
              <div className="feature-content">
                <h3 className="feature-title-alien">{feature.title}</h3>
                <p className="feature-description-alien">{feature.description}</p>
                <div className="feature-metric">
                  <div className="metric-line"></div>
                  <span>{feature.metrics}</span>
                </div>
              </div>
              
              <div className="card-glow"></div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;